// services/orderBoxItem.service.js
//
// Dedicated API client for OrderBoxItem cancel / uncancel / request-cancel custom JSON:API actions.
// Uses jsonApiClient so Sanctum SPA cookie + X-XSRF-TOKEN + 419/401 handling
// flow through the shared axios interceptors. Do NOT introduce bearer-token auth.
import { jsonApiClient } from '@/httpConfig/config/axios.js';
import { PRICE_SEEN_MIN_CENTS } from '@/utils/supplierSuggestion.js';

/**
 * Tag attached to errors that the server flagged as "item locked in active
 * collection plan". The cancel UI uses this to surface the bilingual
 * contact-admin message and to disable Cancel/Edit on the affected item.
 */
export const ORDER_BOX_ITEM_PLAN_LOCKED = 'order-box-item-plan-locked';

/**
 * Tag attached to errors that mean local state is stale (e.g. trying to
 * cancel a non-PENDING item or uncancel a non-CANCELLED item). The caller
 * should surface the server detail and refresh the item.
 */
export const ORDER_BOX_ITEM_STALE_STATE = 'order-box-item-stale-state';

function extractJsonApiDetail(error) {
  return error?.response?.data?.errors?.[0]?.detail || null;
}

function normalizeError(error) {
  const status = error?.response?.status;
  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;

  if (status === 403) {
    normalized.code = ORDER_BOX_ITEM_PLAN_LOCKED;
  } else if (status === 422) {
    normalized.code = ORDER_BOX_ITEM_STALE_STATE;
  }
  return normalized;
}

function extractAttributes(response) {
  // jsonApiClient runs jsona deserialization, so response.data.data is a
  // flat object with id + attributes spread. We return that flat shape so
  // callers can write it straight into the OrderBoxItem repo via .save().
  const data = response?.data?.data;
  if (!data) {
    throw new Error('Empty response from an order-box-item action endpoint');
  }
  // ItemStatus enum values are numeric (PENDING=1, CANCELLED=7, etc.) and the
  // UI compares with `===`. If the backend serializes status as a string
  // ("7" instead of 7), every `currentStatus.value === ItemStatus.CANCELLED`
  // check fails silently and the cancelled badge never appears. Coerce here
  // once so callers don't have to guard against the string/number drift.
  if (data.status !== undefined && data.status !== null) {
    data.status = Number(data.status);
  }
  return data;
}

/**
 * Cancel an OrderBoxItem.
 *
 * @param {string|number} itemId
 * @param {string} [reason] free-text audit note (<= 500 chars, optional)
 * @returns {Promise<object>} the updated item as a flat (deserialized) JSON:API resource
 * @throws Error with `code` set to ORDER_BOX_ITEM_PLAN_LOCKED on 403 or
 *         ORDER_BOX_ITEM_STALE_STATE on 422.
 */
export async function cancelOrderBoxItem(itemId, reason) {
  try {
    const body = reason ? { reason } : {};
    const response = await jsonApiClient.post(`/order-box-items/${itemId}/cancel`, body);
    return extractAttributes(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

/**
 * Undo cancellation of a previously cancelled OrderBoxItem.
 *
 * @param {string|number} itemId
 * @param {string} [reason]
 * @returns {Promise<object>} the updated item as a flat (deserialized) JSON:API resource
 */
export async function uncancelOrderBoxItem(itemId, reason) {
  try {
    const body = reason ? { reason } : {};
    const response = await jsonApiClient.post(`/order-box-items/${itemId}/uncancel`, body);
    return extractAttributes(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

// ── «اطلب الإلغاء» — her cancel request on a line on a live plan ─────────────
//
// BE→FE brief `2026-09-21-member-cancel-request.md`. The door's eligibility
// rule is `canRequestCancel()` in models/commerce/OrderBoxItem.js, which mirrors
// `OrderBoxItemPolicy::requestCancel()` — the door should never be offered on a
// line these codes would answer for.

/**
 * 403 — the line is not hers, or it is no longer on a live plan (the collector
 * released it since her screen loaded). NOT `ORDER_BOX_ITEM_PLAN_LOCKED`: that
 * tag means the opposite — "locked in a plan, ask the admin", which is what
 * this door already is. The right response is to re-read the line, which will
 * offer the plain cancel again if it is hers to take.
 */
export const ORDER_BOX_ITEM_REQUEST_CANCEL_FORBIDDEN = 'order-box-item-request-cancel-forbidden';

/**
 * Ask the club's admin to cancel a line a collector is already out buying.
 *
 * POST /order-box-items/{id}/request-cancel, optional `{ reason }` (<= 500) —
 * the same plain body `cancelOrderBoxItem` sends. Returns the line, now carrying
 * `cancelRequestedAt`, in the same flat shape, so the caller saves it straight
 * into the ORM.
 *
 *  - A repeat call on a line whose request is already waiting is a harmless 200
 *    (no second ping to the admin).
 *  - 422 carries HER OWN ARABIC SENTENCE (`messages.order_box_item.error.cannot_cancel`)
 *    when the line was bought meanwhile — show `error.detail` verbatim.
 *
 * @param {string|number} itemId
 * @param {string} [reason] free-text note for the admin (<= 500 chars, optional)
 * @returns {Promise<object>} the updated item as a flat (deserialized) JSON:API resource
 * @throws Error with `code` set to ORDER_BOX_ITEM_REQUEST_CANCEL_FORBIDDEN on 403
 *         or ORDER_BOX_ITEM_STALE_STATE on 422.
 */
export async function requestCancelOrderBoxItem(itemId, reason) {
  try {
    const body = reason ? { reason } : {};
    const response = await jsonApiClient.post(`/order-box-items/${itemId}/request-cancel`, body);
    return extractAttributes(response);
  } catch (error) {
    const normalized = normalizeError(error);
    if (normalized.status === 403) normalized.code = ORDER_BOX_ITEM_REQUEST_CANCEL_FORBIDDEN;
    throw normalized;
  }
}

// ── The member's supplier suggestion ─────────────────────────────────────────

/**
 * Tag for a 403 on the suggest door: the line moved past suggesting (it was
 * collected, packed or shipped), or it is not hers. Distinct from
 * ORDER_BOX_ITEM_PLAN_LOCKED even though both are 403s — the cancel 403 means
 * "locked in an active plan, ask the admin", which is the wrong sentence here.
 * The right response to this one is to make the door disappear.
 */
export const ORDER_BOX_ITEM_SUGGEST_FORBIDDEN = 'order-box-item-suggest-forbidden';

/** 422 — no shop named, an unrecognised `supplier_id`, or a note over 500. */
export const ORDER_BOX_ITEM_SUGGEST_INVALID = 'order-box-item-suggest-invalid';

/**
 * 409 — the line's question can no longer be answered because the plan closed
 * under her. Her suggestion can ALSO be an answer to a live question, and that
 * half runs in the same transaction, so the two refusals the member's own
 * respond action gives reach this door too.
 */
export const ORDER_BOX_ITEM_SUGGEST_ROUND_CLOSED = 'order-box-item-suggest-round-closed';

function normalizeSuggestError(error) {
  const status = error?.response?.status;
  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;

  if (status === 403) {
    normalized.code = ORDER_BOX_ITEM_SUGGEST_FORBIDDEN;
  } else if (status === 409) {
    normalized.code = ORDER_BOX_ITEM_SUGGEST_ROUND_CLOSED;
  } else if (status === 422) {
    normalized.code = ORDER_BOX_ITEM_SUGGEST_INVALID;
  }
  return normalized;
}

/**
 * Tell the club WHERE to find a line nobody could source — "Trust has it,
 * second floor".
 *
 * POST /order-box-items/{id}/suggest-supplier. A plain body, not a JSON:API
 * document — same shape as `cancel`'s `{ reason }` above, because the backend
 * validates plain snake_case keys.
 *
 * ── THE BODY IS THREE KEYS, AND NOTHING ELSE, EVER ──────────────────────────
 * (oos-hold P1-B, brief `2026-09-15-roster-only-tips-once-per-line.md`, owner
 * answer H-C.)
 *
 *   supplier_id       REQUIRED, an id from `GET /api/v1/suppliers`
 *   note              optional, <= 500
 *   price_seen_cents  optional, integer piasters, >= 100
 *
 * `supplier_name`, `intent` and `any_price` are now REFUSED with a 422 — not
 * ignored. The typed-shop box and "just try again, anywhere" are gone from the
 * product, so this function no longer ACCEPTS those arguments at all rather
 * than filtering them out: a caller that still passes them gets a JS error at
 * the call site instead of an Arabic 422 in front of a member. That is the same
 * discipline `suggestSupplierForProduct` has kept about `intent` since the
 * product door landed.
 *
 * Two behaviours worth knowing at the call site:
 *  - A second POST while one is still open UPDATES it. There is never a second
 *    row, so an "edit my suggestion" affordance is just a re-POST. Once the tip
 *    has been ANSWERED the door is spent for that line's whole life (403) and
 *    `supplierSuggestionUsed` is what tells the app so up front.
 *  - When a question on the line is sitting at `awaiting_member`, the backend
 *    records her ANSWER and writes no tip at all. That branch is TRANSITIONAL
 *    and this app no longer uses it: the fourth reply posts `respond` directly
 *    (`useItemIssue.respond`, oos-hold P3-A part B). Nothing here should reach
 *    an `awaiting_member` line any more.
 *
 * @param {string|number} itemId
 * @param {{supplierId: string|number, note?: string|null,
 *   priceSeenCents?: number|null}} suggestion
 * @returns {Promise<object>} the updated item, flat (deserialized) JSON:API
 * @throws Error with `code` set to one of the three tags above.
 */
export async function suggestSupplierForItem(
  itemId,
  { supplierId, note, priceSeenCents } = {},
) {
  const body = {};

  // REQUIRED. Sent whenever the caller named one; an absent id is left to the
  // backend's own 422 rather than invented here, because a body with no
  // `supplier_id` is the one shape that must never silently become something
  // else.
  if (supplierId != null && supplierId !== '') {
    body.supplier_id = Number(supplierId);
  }

  // Omitted when blank — there is no "clear the note" case here: each POST
  // rewrites the open suggestion whole.
  if (typeof note === 'string' && note.trim() !== '') {
    body.note = note.trim();
  }

  // Piasters, and only ever a whole number at or above the backend's floor.
  // Its absence is her not having named a price, which is the common case, so
  // anything unusable is DROPPED rather than sent — a 422 about a field she
  // left optional would be the request arguing with her.
  if (Number.isInteger(priceSeenCents) && priceSeenCents >= PRICE_SEEN_MIN_CENTS) {
    body.price_seen_cents = priceSeenCents;
  }

  try {
    const response = await jsonApiClient.post(
      `/order-box-items/${itemId}/suggest-supplier`,
      body,
    );
    return extractAttributes(response);
  } catch (error) {
    throw normalizeSuggestError(error);
  }
}

/**
 * Read back JUST her tip and the club's answer to it, for one line.
 *
 * ⚠ CUT — pending the cleanup pass (oos-hold P3-A, owner answer H-O,
 * 2026-09-15). IT HAS NO CALLERS. The gate this existed to open is GONE: the
 * backend deleted the U-40 sparse-fieldset rule on 2026-09-15 (brief
 * `2026-09-15-roster-only-tips-once-per-line.md`, change 5), so an answered tip
 * now rides `supplierSuggestion` on EVERY read of the line. Asking for it
 * separately buys nothing and costs a request per sheet open.
 *
 * Left in place rather than deleted inline, per this repo's no-inline-deletes
 * rule. Do NOT re-introduce a caller: the sparse fieldset this builds is
 * precisely the hazard `OrderBoxItem.updating()` warns about, and the reason it
 * was survivable was that it names no `status`.
 *
 * ── WHY IT EXISTED AS ITS OWN, DELIBERATELY NARROW READ ─────────────────────
 * An ANSWERED suggestion travels only when the client names the attribute in a
 * sparse fieldset (`OrderBoxItemResource::wantsAnsweredSuggestion()`). That gate
 * exists to protect the SHIPPED app, whose reader treated any present value as
 * "still waiting" and would have rendered «اقتراحك وصل للأدمن» over an answered
 * tip for thirty days — the exact opposite of gap U-40. Opting in is therefore
 * the only way she can read the answer, and the decline reason, today.
 *
 * ── AND WHY IT IS NEVER FOLDED INTO A NORMAL ITEM READ ──────────────────────
 * A sparse fieldset returns ONLY the named fields, so widening one of the box
 * reads to carry `supplierSuggestion` would mean enumerating every attribute the
 * app reads off this resource — and silently losing any attribute the backend
 * adds later. Worse, `OrderBoxItem.updating()` nulls every omitted
 * `OMITTED_WHEN_ABSENT` key on any record that mentions `status` (defect U-30),
 * so a sparse read naming `status` would blank `availability`, `lossReason` and
 * `inActivePlan` with no error anywhere — the exact hazard that hook's docblock
 * warns about.
 *
 * This read names ONE field and no `status`, which is why the hook leaves the
 * rest of the line alone and the merge behaves like the partial save it is.
 *
 * @param {string|number} itemId
 * @returns {Promise<object|null>} the `supplierSuggestion` payload, or null when
 *   she has never named a shop for this line (or her tip has aged out).
 */
export async function fetchSupplierSuggestionForItem(itemId) {
  const response = await jsonApiClient.get(`/order-box-items/${itemId}`, {
    params: { 'fields[order-box-items]': 'supplierSuggestion' },
  });

  // Omitted-key contract: absent is how the wire says "nothing to report", and
  // null is how this says it to the ORM — an explicit null so a tip that has
  // aged out of the thirty-day window actually clears the stale receipt.
  return response?.data?.data?.supplierSuggestion ?? null;
}

// ── «أرجّع الصنف» — her post-delivery return request ─────────────────────────
//
// BE→FE brief `2026-09-17-member-return-request.md`; the door's eligibility
// rule lives in utils/supplierReturn.js (canRequestReturn), which mirrors
// `OrderBoxItemPolicy::requestReturn()` — the door should never be offered on
// a line these codes would answer for.

/** 403 — the door was offered on a line the policy refuses (moved on, not
 *  DELIVERED, a Poola line, or the carton stopped taking it back). Not the
 *  member's mistake: the screen should re-read the line and stop offering it. */
export const ORDER_BOX_ITEM_RETURN_FORBIDDEN = 'order-box-item-return-forbidden';

/** 409 — a return is already open on this line (a second tap). */
export const ORDER_BOX_ITEM_RETURN_ALREADY_OPEN = 'order-box-item-return-already-open';

/** 422 — a bad body: no reason, more pieces than are still returnable, a note
 *  over 500 characters, or a photo token that did not resolve. */
export const ORDER_BOX_ITEM_RETURN_INVALID = 'order-box-item-return-invalid';

function normalizeReturnError(error) {
  const status = error?.response?.status;
  const detail = extractJsonApiDetail(error);
  const normalized = new Error(detail || error?.message || 'Request failed');
  normalized.status = status;
  normalized.detail = detail;
  normalized.original = error;

  if (status === 403) {
    normalized.code = ORDER_BOX_ITEM_RETURN_FORBIDDEN;
  } else if (status === 409) {
    normalized.code = ORDER_BOX_ITEM_RETURN_ALREADY_OPEN;
  } else if (status === 422) {
    normalized.code = ORDER_BOX_ITEM_RETURN_INVALID;
  }
  return normalized;
}

/**
 * Ask to return a delivered line — "bring or send the piece back".
 *
 * POST /order-box-items/{id}/request-return. Plain JSON body, snake_case keys
 * — the same shape `suggestSupplierForItem` sends, because the backend
 * validates plain keys here too, not a JSON:API document.
 *
 *   reason        REQUIRED — one of SupplierReturnReason::postDeliveryCases()
 *   qty           REQUIRED — 1..the line's remaining returnable pieces
 *   note          optional, <= 500 characters
 *   photo_token   optional — the token POST /filepond hands back; a
 *                 photo_path is ignored, so this function never accepts one
 *
 * The response is the line itself with its fresh `supplierReturn` payload —
 * `{ status: 'request_received', reasonKey }` — so a caller that `.save()`s it
 * into the ORM needs no separate re-read to show the arriving word.
 *
 * @param {string|number} itemId
 * @param {{reason: string, qty: number, note?: string|null,
 *   photoToken?: string|null}} request
 * @returns {Promise<object>} the updated item, flat (deserialized) JSON:API
 * @throws Error with `code` set to one of the three tags above.
 */
export async function requestReturnForItem(itemId, { reason, qty, note, photoToken } = {}) {
  const body = { reason, qty };

  if (typeof note === 'string' && note.trim() !== '') {
    body.note = note.trim();
  }
  if (typeof photoToken === 'string' && photoToken !== '') {
    body.photo_token = photoToken;
  }

  try {
    const response = await jsonApiClient.post(
      `/order-box-items/${itemId}/request-return`,
      body,
    );
    return extractAttributes(response);
  } catch (error) {
    throw normalizeReturnError(error);
  }
}
